import React from 'react';
import { Paths } from 'shared/enums/paths';
import { TDivisionEmployees, TModerators } from 'shared/types/types';

export type MenuItem = {
  label: React.ReactNode;
  key: React.Key;
  icon?: React.ReactNode;
  path?: MenuKeyLink;
  permissions?: Permissions[];
  indicator?: IndicatorKeys;
  moderators?: (keyof TModerators)[];
};

export type MenuKeyOption = { selectedKeys?: MenuKeys[]; openKeys?: MenuKeys[] };
export type MenuKeyLink =
  | Paths.home
  | Paths.references
  | Paths.consultations
  | Paths.rvd
  | Paths.vacation
  | Paths.vacationApplication
  | Paths.vacationSchedule
  | Paths.reports
  | Paths.controls
  | Paths.absence
  | Paths.absenceSupervisor
  | Paths.substitutions
  | Paths.news
  | Paths.faq
  | Paths.overtime
  | Paths.polls
  | Paths.dayoff
  | Paths.cancelDayoff
  | Paths.vacationScheduleSubordinates
  | Paths.dayoffSupervisor
  | Paths.dayoffCancelSupervisor;

export type MenuKeysMap = Record<MenuKeyLink, MenuKeyOption>;

export enum IndicatorKeys {
  consultation = 'consultation',
  vacation = 'vacation',
  vacationSchedule = 'vacationSchedule',
  absence = 'absence',
  document = 'document',
  timeOff = 'timeOff',
  timeOffCanceled = 'timeOffCanceled',
}

export enum MenuKeys {
  home = 'home',
  references = '/references',
  consultations = '/consultations',
  rvd = '/rvd',
  vacation = '/vacation',
  vacationApplication = '/vacation-application',
  vacationScheduleSubordinates = '/vacation-schedule-subordinates',
  vacationSchedule = '/vacation-schedule',
  reports = '/reports',
  controls = '/controls',
  absence = '/absence',
  absenceSupervisor = '/absence-supervisor',
  substitutions = '/substitutions',
  news = '/news',
  faq = '/faq',
  overtime = '/overtime',
  polls = '/polls',
  dayoff = '/dayoff',
  cancelDayoff = '/cancelDayoff',
  dayoffSupervisor = '/dayoff-supervisor',
  dayoffCancelSupervisor = '/dayoff-cancel-supervisor',
}

export enum Permissions {
  'hr.consultation.access' = 'hr.consultation.access',
  'hr.document.access' = 'hr.document.access',
  'hr.employee-management.get-employees' = 'hr.employee-management.get-employees',
  'hr.employee-management.manage-blocking-employee' = 'hr.employee-management.manage-blocking-employee',
  'hr.employee-management.reset-employee-password' = 'hr.employee-management.reset-employee-password',
  'hr.report.access' = 'hr.report.access',
  'hr.vacation-application.access' = 'hr.vacation-application.access',
  'hr.vacation-application.my.approve' = 'hr.vacation-application.my.approve',
  'hr.vacation-application.my.archive.list' = 'hr.vacation-application.my.archive.list',
  'hr.vacation-application.my.list' = 'hr.vacation-application.my.list',
  'hr.vacation-application.my.reject' = 'hr.vacation-application.my.reject',
  'hr.vacation-application.new.list' = 'hr.vacation-application.new.list',
  'hr.vacation-application.new.my.other.change-responsible' = 'hr.vacation-application.new.my.other.change-responsible',
  'hr.vacation-application.new.take-to-work' = 'hr.vacation-application.new.take-to-work',
  'hr.vacation-application.other.archive.list' = 'hr.vacation-application.other.archive.list',
  'hr.vacation-application.other.list' = 'hr.vacation-application.other.list',
  'hr.vacation-schedule.access' = 'hr.vacation-schedule.access',
  'hr.vacation-schedule.allow-scheduling' = 'hr.vacation-schedule.allow-scheduling',
  'hr.vacation-schedule.download-file' = 'hr.vacation-schedule.download-file',
  'hr.vacation-schedule.employee.permits' = 'hr.vacation-schedule.employee.permits',
  'hr.vacation-schedule.employee.requests.cancel-mark-to-sending' = 'hr.vacation-schedule.employee.requests.cancel-mark-to-sending',
  'hr.vacation-schedule.employee.requests.create' = 'hr.vacation-schedule.employee.requests.create',
  'hr.vacation-schedule.employee.requests.mark-to-sending' = 'hr.vacation-schedule.employee.requests.mark-to-sending',
  'hr.vacation-schedule.employee.requests.reject' = 'hr.vacation-schedule.employee.requests.reject',
  'hr.vacation-schedule.employees-with-requests' = 'hr.vacation-schedule.employees-with-requests',
  'hr.vacation-schedule.send-to-zup' = 'hr.vacation-schedule.send-to-zup',
  'hr.absence.access' = 'hr.absence.access',
  'accountant.document.access' = 'accountant.document.access',
  'supervisor.vacation-application.access' = 'supervisor.vacation-application.access',
  'supervisor.vacation-application.approve' = 'supervisor.vacation-application.approve',
  'supervisor.vacation-application.reject' = 'supervisor.vacation-application.reject',
  'supervisor.vacation-application.request' = 'supervisor.vacation-application.request',
  'supervisor.vacation-application.requests' = 'supervisor.vacation-application.requests',
  'supervisor.absence-supervisor.access' = 'supervisor.absence-supervisor.access',
  'timekeeper.absence-supervisor.access' = 'timekeeper.absence-supervisor.access',
  'hr.settings.access' = 'hr.settings.access',
  'hr.settings.get' = 'hr.settings.get',
  'hr.settings.change' = 'hr.settings.change',
  'hr.time-off.access' = 'hr.time-off.access',
  'hr.substitution.access' = 'hr.substitution.access',
  'hr.additional-income.overtime.access' = 'hr.additional-income.overtime.access',
  'hr.additional-income.wow.access' = 'hr.additional-income.wow.access',
}
